import { Component, CSSProperties, ReactNode } from "react";
import { getAmapSearchLanguageOptions } from "../../../../../shared/utilty/amap-language";
import { gcj02ToWgs84, IGeoPoint } from "../../../../../shared/utilty/coordinate-transform";
import { readLngLat } from "../../../../../shared/utilty/coordinate-value";
import { ICoords } from "../google-maps/google-maps-item";

/**
 * Place-like shape that AmapBodyWrapper.selectPlace() consumes, mirroring the subset of
 * google.maps.places.PlaceResult that google-map-search.tsx hands back to
 * google-maps-body-wrapper.tsx (geometry.location as lat()/lng() accessors, formatted_address,
 * name). By the time a place reaches this shape its coordinates have already been converted
 * from Amap's native GCJ-02 back to WGS-84 (gcj02ToWgs84), so downstream Kuika code keeps
 * treating every coordinate it sees as WGS-84 regardless of the active map provider.
 */
interface IAmapPlaceLocation {
  lat: () => number;
  lng: () => number;
}

export interface IAmapPlaceGeometry {
  location: IAmapPlaceLocation;
}

export interface IAmapPlaceLike {
  geometry: IAmapPlaceGeometry;
  formatted_address?: string;
  name?: string;
}

// AMap.AutoComplete and AMap.PlaceSearch are loaded by amap-loader.ts but are not declared by
// @amap/amap-jsapi-types (only Map, Marker, LngLat, Circle, Pixel, Size, Icon, TileLayer and
// MarkerClusterer are typed there). These minimal interfaces describe exactly the constructor
// options, methods and result fields this component uses, so the plugins can be consumed
// without ever annotating `any`.
interface IAMapAutoCompletePoi {
  id?: string;
  name?: string;
  address?: string | string[];
  location?: AMap.LngLat;
}

interface IAMapAutoCompleteSelectEvent {
  poi: IAMapAutoCompletePoi;
}

interface IAMapAutoCompleteOptions {
  input: HTMLInputElement;
  /** Result language on World Map projects (amap-language.ts). */
  languageCode?: string;
}

interface IAMapAutoComplete {
  on(event: "select", callback: (e: IAMapAutoCompleteSelectEvent) => void): void;
  off(event: "select", callback: (e: IAMapAutoCompleteSelectEvent) => void): void;
}

interface IAMapPlaceSearchPoi {
  id?: string;
  name?: string;
  address?: string;
  location?: AMap.LngLat;
}

interface IAMapPlaceSearchPoiList {
  pois: IAMapPlaceSearchPoi[];
}

interface IAMapPlaceSearchDetailsResult {
  poiList?: IAMapPlaceSearchPoiList;
}

interface IAMapPlaceSearchOptions {
  pageSize?: number;
  /** Result language on World Map projects (amap-language.ts). */
  languageCode?: string;
}

interface IAMapPlaceSearch {
  getDetails(id: string, callback: (status: string, result: IAMapPlaceSearchDetailsResult) => void): void;
}

interface IAMapSearchPluginNamespace {
  AutoComplete: new (opts: IAMapAutoCompleteOptions) => IAMapAutoComplete;
  PlaceSearch: new (opts?: IAMapPlaceSearchOptions) => IAMapPlaceSearch;
}

export interface AmapAutoCompleteProps {
  map?: AMap.Map;
  maps?: typeof AMap;
  addplace: (place: IAmapPlaceLike) => void;
  hasSearchBox?: boolean;
  onChangeLocation?: (location: ICoords) => void;
}

/**
 * Amap equivalent of google-map-search.tsx. AMap.AutoComplete binds directly to the input
 * element (same UX as google.maps.places.Autocomplete) and shows its own suggestion dropdown;
 * selecting a suggestion fires the "select" event with a POI. When the POI already carries a
 * location (the common case) it is used directly; otherwise AMap.PlaceSearch.getDetails is used
 * as a fallback to resolve the full POI (id -> details) before continuing.
 */
export class AmapAutoComplete extends Component<AmapAutoCompleteProps, Record<string, never>> {
  private autoComplete: IAMapAutoComplete | undefined;

  private placeSearch: IAMapPlaceSearch | undefined;

  private searchInput: HTMLInputElement | null = null;

  constructor(props: AmapAutoCompleteProps) {
    super(props);
    this.clearSearchBox = this.clearSearchBox.bind(this);
    this.onPlaceSelected = this.onPlaceSelected.bind(this);
  }

  componentDidMount(): void {
    const { maps } = this.props;
    if (!maps || !this.searchInput) return;

    const plugins: IAMapSearchPluginNamespace = maps as unknown as IAMapSearchPluginNamespace;
    this.autoComplete = new plugins.AutoComplete({ input: this.searchInput, ...getAmapSearchLanguageOptions() });
    this.autoComplete.on("select", this.onPlaceSelected);
    this.placeSearch = new plugins.PlaceSearch({ ...getAmapSearchLanguageOptions() });
  }

  componentWillUnmount(): void {
    this.autoComplete?.off("select", this.onPlaceSelected);
  }

  private formatAddress(poi: IAMapAutoCompletePoi | IAMapPlaceSearchPoi): string | undefined {
    if (Array.isArray(poi.address)) return poi.address.join(" ");
    return poi.address ?? poi.name;
  }

  private applyPoi(poi: IAMapAutoCompletePoi | IAMapPlaceSearchPoi): void {
    // A LngLat instance from AutoComplete, or a plain {lat, lng} object from PlaceSearch.getDetails.
    const gcjLocation: IGeoPoint | null = readLngLat(poi.location);
    if (!gcjLocation) return;

    const wgs84: IGeoPoint = gcj02ToWgs84(gcjLocation.latitude, gcjLocation.longitude);
    const { map, onChangeLocation, addplace } = this.props;

    if (map) {
      map.setCenter([gcjLocation.longitude, gcjLocation.latitude]);
    }
    if (onChangeLocation) {
      onChangeLocation({ latitude: wgs84.latitude, longitude: wgs84.longitude });
    }

    const place: IAmapPlaceLike = {
      geometry: {
        location: {
          lat: () => wgs84.latitude,
          lng: () => wgs84.longitude
        }
      },
      formatted_address: this.formatAddress(poi),
      name: poi.name
    };
    addplace(place);
    this.searchInput?.blur();
  }

  private resolveViaPlaceSearch(poi: IAMapAutoCompletePoi): void {
    const placeSearch = this.placeSearch;
    if (!poi.id || !placeSearch) return;

    placeSearch.getDetails(poi.id, (status: string, result: IAMapPlaceSearchDetailsResult) => {
      if (status !== "complete") return;
      const detail: IAMapPlaceSearchPoi | undefined = result.poiList?.pois[0];
      if (!detail) return;
      this.applyPoi(detail);
    });
  }

  private onPlaceSelected(e: IAMapAutoCompleteSelectEvent): void {
    const poi: IAMapAutoCompletePoi | undefined = e?.poi;
    if (!poi) return;

    if (poi.location) {
      this.applyPoi(poi);
      return;
    }
    this.resolveViaPlaceSearch(poi);
  }

  clearSearchBox(): void {
    if (this.searchInput) {
      this.searchInput.value = "";
    }
  }

  private getDisplay(): CSSProperties {
    if (this.props.map !== undefined && this.props.maps !== undefined && this.props.hasSearchBox === true) {
      return {};
    }
    return { display: "none" };
  }

  render(): ReactNode {
    return (
      <input
        style={this.getDisplay()}
        className="form-control marb15"
        ref={(ref: HTMLInputElement | null) => {
          this.searchInput = ref;
        }}
        required
        name="propertyGPS"
        type="text"
        onFocus={this.clearSearchBox}
      />
    );
  }
}

